import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

// Tipos de payload para eventos Socket.io
export interface StatusChangeResult {
  success: boolean;
  reserva_id: string;
  status_anterior: string;
  status_novo: string;
  motivo: string;
  error?: string;
}

export interface ReservationStatusChangedPayload {
  type: 'status_change';
  reserva_id: string;
  timestamp: string;
  data: StatusChangeResult;
}

export interface StatsUpdatePayload {
  type: 'stats_update';
  timestamp: string;
  data: unknown;
}

export interface SystemUpdatePayload {
  type: string;
  timestamp: string;
  [key: string]: unknown;
}

export interface SocketErrorPayload {
  type: string;
  message: string;
  error?: unknown;
}

// Extendemos para a sala do usuário quando o servidor incluir user_id
export interface UserReservationUpdatePayload extends ReservationStatusChangedPayload {
  data: StatusChangeResult & { user_id: string };
}

interface SocketHookReturn {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
  lastMessage: unknown;
  sendMessage: (event: string, data: unknown) => void;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
}

interface UseSocketOptions {
  autoConnect?: boolean;
  userId?: string;
  token?: string;
  onStatusChange?: (data: ReservationStatusChangedPayload) => void;
  onSystemUpdate?: (data: StatsUpdatePayload | SystemUpdatePayload) => void;
  onError?: (error: SocketErrorPayload) => void;
}

export const useSocket = (options: UseSocketOptions = {}): SocketHookReturn => {
  const {
    autoConnect = true,
    userId,
    token,
    onStatusChange,
    onSystemUpdate,
    onError
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<unknown>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!autoConnect) return;

    const mode = import.meta.env.MODE;
    const rawServerUrl = import.meta.env.VITE_API_URL || '';
    const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const serverUrl = rawServerUrl || runtimeOrigin || 'http://localhost:3001';
    const normalizedServerUrl = mode !== 'development' && serverUrl.startsWith('http://')
      ? serverUrl.replace('http://', 'https://')
      : serverUrl;
    
    socketRef.current = io(normalizedServerUrl, {
      // Usar polling primeiro, depois tentar websocket
      transports: ['polling', 'websocket'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      // Forçar nova conexão em caso de erro
      forceNew: false,
      // Configurações adicionais para estabilidade
      upgrade: true,
      rememberUpgrade: false
    });

    const socket = socketRef.current;

    // Event listeners de conexão
    socket.on('connect', () => {
      
      setIsConnected(true);
      setConnectionError(null);

      // Autenticar se tiver userId e token
      if (userId && token) {
        socket.emit('authenticate', { userId, token });
      }
    });

    socket.on('disconnect', (reason) => {
      
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        // Servidor desconectou o cliente, tentar reconectar
        socket.connect();
      }
    });

    socket.on('connect_error', (error: Error & { type?: string; description?: string }) => {
      
      const errorMessage = error.message || 'Erro de conexão desconhecido';
      
      // Tentar identificar o tipo de erro
      if (error.type === 'TransportError' || error.description?.includes('websocket')) {
        
        setConnectionError('Problema de rede - tentando reconectar...');
      } else {
        setConnectionError(errorMessage);
      }
      
      setIsConnected(false);
      
      // Notificar callback de erro se existir
      if (onError) {
        onError({ type: 'connection_error', message: errorMessage, error });
      }
    });

    socket.on('auth_error', (data: { message: string }) => {
      
      setConnectionError(data.message);
      if (onError) onError({ type: 'auth_error', message: data.message, error: undefined });
    });

    // Event listeners específicos da aplicação
    socket.on('reservation_status_changed', (data: ReservationStatusChangedPayload) => {
      
      setLastMessage({ type: 'status_change', payload: data });
      if (onStatusChange) onStatusChange(data);
    });

    socket.on('system_update', (data: SystemUpdatePayload) => {
      
      setLastMessage({ type: 'system_update', payload: data });
      if (onSystemUpdate) onSystemUpdate(data);
    });

    socket.on('user_notification', (data: SystemUpdatePayload) => {
      
      setLastMessage({ type: 'user_notification', ...data });
    });

    socket.on('stats_updated', (data: StatsUpdatePayload) => {
      
      setLastMessage({ type: 'stats_updated', ...data });
    });

    socket.on('system_error', (data: SocketErrorPayload) => {
      
      setLastMessage({ type: 'system_error', ...data });
      if (onError) onError(data);
    });

    socket.on('heartbeat', (data: SystemUpdatePayload) => {
      
      setLastMessage({ type: 'heartbeat', ...data });
    });

    socket.on('force_disconnect', (data: { reason: string }) => {
      
      setConnectionError(data.reason);
    });

    // Cleanup na desmontagem
    return () => {
      
      socket.disconnect();
      socketRef.current = null;
    };
  }, [autoConnect, userId, token, onError, onStatusChange, onSystemUpdate]);

  // Funções utilitárias
  const sendMessage = (event: string, data: unknown) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit(event, data);
    }
  };

  const joinRoom = (room: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join_room', room);
      
    }
  };

  const leaveRoom = (room: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave_room', room);
      
    }
  };

  return {
    socket: socketRef.current,
    isConnected,
    connectionError,
    lastMessage,
    sendMessage,
    joinRoom,
    leaveRoom
  };
};

// Hook específico para administradores
export const useAdminSocket = (userId?: string, token?: string) => {
  const [reservationUpdates, setReservationUpdates] = useState<unknown[]>([]);
  const [systemAlerts, setSystemAlerts] = useState<unknown[]>([]);
  const [connectionStats, setConnectionStats] = useState<unknown>(null);

  const handleStatusChange = useCallback((data: ReservationStatusChangedPayload) => {
    setReservationUpdates(prev => [data, ...prev].slice(0, 50));
  }, []);

  const handleSystemUpdate = useCallback((data: StatsUpdatePayload | SystemUpdatePayload) => {
    if (data.type === 'stats_update') {
      setConnectionStats((data as StatsUpdatePayload).data);
    } else {
      setSystemAlerts(prev => [data, ...prev].slice(0, 20));
    }
  }, []);

  const handleError = useCallback((error: SocketErrorPayload) => {
    setSystemAlerts(prev => [
      { type: 'error', message: error.message || 'Erro desconhecido', timestamp: new Date().toISOString() },
      ...prev
    ].slice(0, 20));
  }, []);

  const socket = useSocket({ userId, token, onStatusChange: handleStatusChange, onSystemUpdate: handleSystemUpdate, onError: handleError });
  // Destruturação para efeitos com deps precisas
  const { isConnected: isAdminConnected, joinRoom: adminJoinRoom } = socket;

  // Entrar automaticamente na sala de admin
  useEffect(() => {
    if (isAdminConnected) {
      adminJoinRoom('admin');
    }
  }, [isAdminConnected, adminJoinRoom]);

  return {
    ...socket,
    reservationUpdates,
    systemAlerts,
    connectionStats,
    clearAlerts: () => setSystemAlerts([]),
    clearUpdates: () => setReservationUpdates([])
  };
};

// Hook específico para usuários
export const useUserSocket = (userId?: string, token?: string) => {
  const [userNotifications, setUserNotifications] = useState<unknown[]>([]);
  const [reservationUpdates, setReservationUpdates] = useState<unknown[]>([]);

  const socket = useSocket({
    userId,
    token,
    onStatusChange: (data: UserReservationUpdatePayload) => {
      if (data.data.user_id === userId) {
        setReservationUpdates(prev => [data, ...prev].slice(0, 10));
      }
    },
    onSystemUpdate: (data: SystemUpdatePayload) => {
      if (data.type === 'user_notification') {
        setUserNotifications(prev => [data, ...prev].slice(0, 10));
      }
    }
  });
  // Destruturação para efeito de sala de usuário
  const { isConnected: isUserConnected, joinRoom: userJoinRoom } = socket;

  useEffect(() => {
    if (isUserConnected && userId) {
      userJoinRoom(`user:${userId}`);
    }
  }, [isUserConnected, userJoinRoom, userId]);

  return {
    ...socket,
    userNotifications,
    reservationUpdates,
    clearNotifications: () => setUserNotifications([]),
    clearUpdates: () => setReservationUpdates([])
  };
};

// Hook para monitorar uma reserva específica
export const useReservationSocket = (reservationId: string, userId?: string, token?: string) => {
  const [reservationStatus, setReservationStatus] = useState<unknown>(null);
  const [statusHistory, setStatusHistory] = useState<unknown[]>([]);

  const socket = useSocket({
    userId,
    token,
    onStatusChange: (data) => {
      if (data.reserva_id === reservationId) {
        setReservationStatus(data.data);
        setStatusHistory(prev => [data, ...prev].slice(0, 20));
      }
    }
  });
  // Destruturação para efeitos de sala de reserva
  const { isConnected: isResConnected, joinRoom: resJoinRoom, leaveRoom: resLeaveRoom } = socket;

  useEffect(() => {
    if (isResConnected && reservationId) {
      resJoinRoom(`reserva:${reservationId}`);
    }
    // Sair da sala quando o componente for desmontado
    return () => {
      if (isResConnected && reservationId) {
        resLeaveRoom(`reserva:${reservationId}`);
      }
    };
  }, [isResConnected, resJoinRoom, resLeaveRoom, reservationId]);

  return {
    ...socket,
    reservationStatus,
    statusHistory,
    clearHistory: () => setStatusHistory([])
  };
}; 
